import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useIncomeListHeader } from "hooks/Logistic/use-income-list-header";
import { useDisable } from "hooks/use-disable";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { FieldArrayWithId, useFormContext, useWatch } from "react-hook-form";
import { IDeliveryTrip } from "types/Logistic/deliveryTrip";
import MenuIcon from "@mui/icons-material/Menu";
import ItemCell from "./ItemCell";
import ControlledNumberTextField from "components/Controller/ControlledNumberTextField";
import { formatNumber } from "utils/Global";
import CloseIcon from "@mui/icons-material/Close";
import PreVatAmountCell from "./PreVatAmountCell";
// import WithholdingTaxCell from "./WithholdingTaxCell";
// import VatPercentageCell from "./VatPercentageCell";
import UomCell from "./UomCell";

const PricePerUnitDisplay = ({ index }: { index: number }) => {
  const { control } = useFormContext<IDeliveryTrip>();
  const pricePerUnit = useWatch({
    control,
    name: `income_list.${index}.price_per_unit`,
  });
  return <Typography>{formatNumber(pricePerUnit ?? 0)}</Typography>;
};

type Props = {
  fields: FieldArrayWithId<IDeliveryTrip, "income_list", "id">[];
  dragEndHandler: (result: DropResult) => void;
  openDeleteItemConfirmationHandler: (index: number) => void;
  isCancellable?: boolean;
};

const IncomeListTable = ({
  fields,
  dragEndHandler,
  openDeleteItemConfirmationHandler,
  isCancellable,
}: Props) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<IDeliveryTrip>();
  const [disabled] = useDisable();

  // 86d37db3t — no per-status gating; also editable in Create mode. Match
  // the parent form's rule (drop `!id` and status-set gate). `!isCancellable`
  // stays: a DT that already has a ใบวางบิล should not have its lines re-priced.
  const disabledStatus = !isCancellable;

  const headers = useIncomeListHeader(disabledStatus);

  return (
    <TableContainer>
      <Table
        sx={{
          width: "100%",
          tableLayout: "fixed",
          borderCollapse: "separate",
        }}
        aria-label="simple table"
      >
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "secondary.main",
              width: "100%",
            }}
          >
            {headers.map((header, index) => {
              const isLast = index === headers.length - 1;
              return (
                <TableCell
                  align={header.align}
                  key={index}
                  sx={{
                    px: 1,
                    py: 1.5,
                    width: isLast ? "auto" : header.width,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      // width: header.width,
                      justifyContent: header.align,
                    }}
                  >
                    <Typography fontWeight={600}>{header.thaiLabel}</Typography>
                    {header.required && (
                      <Typography
                        fontSize={14}
                        color={"error.main"}
                        fontWeight={600}
                      >
                        *
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <DragDropContext onDragEnd={dragEndHandler}>
          <Droppable
            droppableId="droppable"
            isDropDisabled={disabled || disabledStatus}
          >
            {(provided) => (
              <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                {fields.map((field, index) => (
                  <Draggable
                    key={field.unique_id}
                    draggableId={"income-" + field.unique_id}
                    index={index}
                    isDragDisabled={disabled || disabledStatus}
                  >
                    {(provided) => (
                      <TableRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          ...provided.draggableProps.style,
                          breakInside: "avoid",
                        }}
                      >
                        <TableCell width={headers[0]?.width}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            {!disabled && !disabledStatus && (
                              <MenuIcon
                                fontSize="small"
                                sx={{ color: "#BDBDBD" }}
                              />
                            )}
                            <Typography>{index + 1}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell
                          align={headers[1]?.align}
                          width={headers[1]?.width}
                        >
                          <Box width="100%">
                            <ItemCell
                              field={field}
                              index={index}
                              disabledStatus={disabledStatus}
                            />
                          </Box>
                        </TableCell>
                        <TableCell
                          align={headers[2]?.align}
                          width={headers[2]?.width}
                        >
                          <Box
                            width="100%"
                            mt={
                              Boolean(
                                errors?.income_list &&
                                errors.income_list[index]?.qty,
                              )
                                ? 3
                                : 0
                            }
                          >
                            {!disabled && !disabledStatus ? (
                              <ControlledNumberTextField
                                fullWidth
                                control={control}
                                name={`income_list.${index}.qty`}
                                error={Boolean(
                                  errors?.income_list &&
                                  errors.income_list[index]?.qty,
                                )}
                                helperText={
                                  errors?.income_list &&
                                  errors.income_list[index]?.qty?.message
                                }
                                textAlign="right"
                              />
                            ) : (
                              <Typography>{formatNumber(field.qty)}</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          align={headers[3]?.align}
                          width={headers[3]?.width}
                        >
                          <Box width="100%">
                            <UomCell
                              field={field}
                              index={index}
                              disabledStatus={disabledStatus}
                            />
                          </Box>
                        </TableCell>
                        <TableCell
                          align={headers[4]?.align}
                          width={headers[4]?.width}
                        >
                          <Box
                            width="100%"
                            mt={
                              Boolean(
                                errors?.income_list &&
                                errors.income_list[index]?.price_per_unit,
                              )
                                ? 3
                                : 0
                            }
                          >
                            {!disabled && !disabledStatus ? (
                              <ControlledNumberTextField
                                fullWidth
                                control={control}
                                name={`income_list.${index}.price_per_unit`}
                                error={Boolean(
                                  errors?.income_list &&
                                  errors.income_list[index]?.price_per_unit,
                                )}
                                helperText={
                                  errors?.income_list &&
                                  errors.income_list[index]?.price_per_unit
                                    ?.message
                                }
                              />
                            ) : (
                              <PricePerUnitDisplay index={index} />
                            )}
                          </Box>
                        </TableCell>
                        {/* <TableCell align={headers[5]?.align}>
                          <Box width={headers[5]?.width}>
                            {!disabled && !disabledStatus ? (
                              <ControlledNumberTextField
                                fullWidth
                                control={control}
                                name={`income_list.${index}.discount`}
                                error={Boolean(
                                  errors?.income_list &&
                                    errors.income_list[index]?.discount
                                )}
                              />
                            ) : (
                              <Typography>
                                {formatNumber(field.discount)}
                              </Typography>
                            )}
                          </Box>
                        </TableCell> */}
                        {/* <TableCell align={headers[6]?.align}>
                          <Box width={headers[6]?.width}>
                            <VatPercentageCell
                              field={field}
                              index={index}
                              disabledStatus={disabledStatus}
                            />
                          </Box>
                        </TableCell> */}
                        <TableCell
                          align={headers[5]?.align}
                          width={headers[5]?.width}
                        >
                          <Box width="100%">
                            <PreVatAmountCell field={field} index={index} />
                          </Box>
                        </TableCell>
                        {/* <TableCell align={headers[8]?.align}>
                          <Box width={headers[8]?.width}>
                            <WithholdingTaxCell
                              field={field}
                              index={index}
                              disabledStatus={disabledStatus}
                            />
                          </Box>
                        </TableCell> */}
                        <TableCell width={"auto"}>
                          {!disabled && !disabledStatus && (
                            <Box width="100%">
                              {![157, 158].includes(field.item_id) && (
                                <IconButton
                                  aria-label="delete"
                                  size="small"
                                  sx={{ ml: 1, color: "rgba(0, 0, 0, 0.54)" }}
                                  onClick={() =>
                                    openDeleteItemConfirmationHandler(index)
                                  }
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
      </Table>
    </TableContainer>
  );
};

export default IncomeListTable;
